import type { FormValues } from '@/lib/types';

/** Firestore / UI metadata that must never be sent to evaluation APIs (Zod allows only string | number | null). */
const METADATA_KEYS = new Set([
  'id',
  'type',
  'date',
  'createdAt',
  'updatedAt',
  'clientId',
]);

/**
 * Merge only scalar string/number/null fields from an assessment-like object into the SOP form template.
 * Strips Firestore Timestamps and other objects so `formValues` state stays API-safe.
 */
export function mergeAssessmentScalarsIntoTemplate(template: FormValues, source: Record<string, unknown>): FormValues {
  const next: FormValues = { ...template };
  for (const key of Object.keys(template)) {
    if (!(key in source)) continue;
    const v = source[key];
    if (v === null || typeof v === 'string' || typeof v === 'number') {
      next[key] = v;
    }
  }
  return next;
}

/**
 * Strip metadata, VGon image payloads and Remote Assessment capture-source markers, and non-JSON-scalar values before POSTing to `/assessments/*` routes.
 */
export function sanitizeSopFormValuesForApi(formValues: FormValues): Record<string, string | number | null> {
  const out: Record<string, string | number | null> = {};
  for (const [key, value] of Object.entries(formValues)) {
    if (METADATA_KEYS.has(key)) continue;
    if (key.endsWith('_image') || key.endsWith('_captureSource')) continue;
    if (value === undefined) continue;
    if (value !== null && typeof value !== 'string' && typeof value !== 'number') continue;
    out[key] = value;
  }
  return out;
}
